import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import GrammarRulesExercise from './GrammarRulesExercise';
import './GrammarRulesEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const GrammarRulesEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { content, title, level } = node.attrs;

  const isStudent = (() => {
    try {
      // Check if we're in student mode by looking for student-specific attributes
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      
      // Check if we're in a student dashboard context
      const isStudentDashboard = typeof document !== 'undefined' && 
        (window.location.pathname.includes('/student-dashboard') || 
         window.location.pathname.includes('/student-profile'));
      
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  if (!content) {
    return (
      <NodeViewWrapper>
        <div className="grammar-rules-embed-error">
          <p>Error: Invalid Grammar Rules exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editor && getPos) {
      editor.chain().focus().insertContentAt(getPos(), '\n').run();
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editor && getPos) {
      editor.chain().focus().insertContentAt(getPos() + node.nodeSize, '\n').run();
    }
  };

  // Drag handlers for visual feedback + cross-editor payload
  const handleDragStart = (e) => {
    try {
      // Visuals
      e.target.style.outline = 'none';
      e.target.style.border = 'none';
      e.target.style.boxShadow = 'none';
      e.target.classList.add('dragging');

      // Mark allowed operation and attach payload for cross-editor drop
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'copyMove';
        const payload = {
          type: 'grammarRulesExercise',
          attrs: { content, title, level },
          // пометим исходный редактор
          sourceEditorId: (() => {
            try {
              const root = editor?.view?.dom;
              return root?.getAttribute?.('data-editor-id') || null;
            } catch (_) { return null; }
          })()
        };
        try {
          e.dataTransfer.setData('application/x-novas-node', JSON.stringify(payload));
        } catch (_) {
          // Fallback
          e.dataTransfer.setData('text/plain', JSON.stringify(payload));
        }
        // Use a transparent drag image to avoid UI flicker
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 1; canvas.height = 1;
          const ctx = canvas.getContext('2d');
          if (ctx) { ctx.clearRect(0, 0, 1, 1); }
          e.dataTransfer.setDragImage(canvas, 0, 0);
        } catch (_) {}
      }
    } catch (_) {}
  };

  const handleDragEnd = (e) => {
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  return (
    <NodeViewWrapper className="grammar-rules-embed" contentEditable={false} draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="grammar-rules-exercise-embed" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {!isExpanded ? (
          <div className="grammar-rules-preview unified-exercise-preview" onClick={() => { setIsExpanded(true); updateState({ expanded: true }); }} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="embed-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div className="unified-preview-main">
              <div className="unified-preview-title">{title}</div>
              <div className="unified-preview-meta">
                Level: {level || 'B1'}
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Add to homework"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: {
                        type: 'grammarRules',
                        attrs: { content, title, level }
                      }
                    }));
                  } catch (error) {
                    console.error('Grammar Rules: Failed to dispatch event', error);
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="embed-expanded" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <GrammarRulesExercise
              content={content}
              title={title}
              level={level}
              onClose={() => { setIsExpanded(false); updateState({ expanded: false }); }}
              updateAttributes={updateAttributes}
              node={node}
              editor={editor}
              getPos={getPos}
              isStudent={isStudent}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default GrammarRulesEmbed; 